/**
 * Report date range presets. All ranges use GMT via date-fns-tz.
 * We use UTC-safe helpers (startOfYearUTC, etc.) because date-fns startOfYear/startOfMonth
 * use the system timezone; these use getUTC* + zonedTimeToUtc so boundaries are correct everywhere.
 */
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

export enum ReportDatePreset {
    YTD = 'YTD',
    QTD = 'QTD',
    MTD = 'MTD',
    LAST_QUARTER = 'LAST_QUARTER',
    LAST_MONTH = 'LAST_MONTH',
    LAST_12_MONTHS = 'LAST_12_MONTHS',
}

const reportDatePresetTranslationKeys: Record<ReportDatePreset, string> = {
    [ReportDatePreset.YTD]: 'reportFilters.datePreset.YTD',
    [ReportDatePreset.QTD]: 'reportFilters.datePreset.QTD',
    [ReportDatePreset.MTD]: 'reportFilters.datePreset.MTD',
    [ReportDatePreset.LAST_QUARTER]: 'reportFilters.datePreset.LastQuarter',
    [ReportDatePreset.LAST_MONTH]: 'reportFilters.datePreset.LastMonth',
    [ReportDatePreset.LAST_12_MONTHS]: 'reportFilters.datePreset.Last12Months',
};

export function getReportDatePresetTranslationKey(
    preset: string | undefined | null,
): string | null {
    if (!preset || preset === 'CUSTOM') {
        return null;
    }

    if (preset in reportDatePresetTranslationKeys) {
        return reportDatePresetTranslationKeys[preset as ReportDatePreset];
    }

    return null;
}

export function getReportDateChipLabel(
    args: {
        datePreset?: string | null;
        formattedRangeLabel: string;
    },
    t: (key: string) => string,
): string {
    const presetTranslationKey = getReportDatePresetTranslationKey(
        args.datePreset,
    );

    return presetTranslationKey
        ? t(presetTranslationKey)
        : args.formattedRangeLabel;
}

export function formatReportDateRangeLabel(
    dateFrom: Date,
    dateTo: Date,
): string {
    const formatter = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });

    return `${formatter.format(dateFrom)} - ${formatter.format(dateTo)}`;
}

export interface ReportDateRange {
    from: Date;
    to: Date;
}

const UTC = 'Etc/UTC';

/**
 * Build a UTC moment from (y, m, d, h, min, s, ms) interpreted as GMT.
 */
function inUTC(
    y: number,
    m: number,
    d: number,
    h = 0,
    min = 0,
    s = 0,
    ms = 0,
): Date {
    return zonedTimeToUtc(new Date(y, m, d, h, min, s, ms), UTC);
}

/** Start of the given date's day in UTC (00:00:00.000). */
export function startOfDayGMT(date: Date): Date {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    const d = date.getUTCDate();
    return inUTC(y, m, d, 0, 0, 0, 0);
}

/** End of the given date's day in UTC (23:59:59.999). */
function endOfDayGMT(date: Date): Date {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    const d = date.getUTCDate();
    return inUTC(y, m, d, 23, 59, 59, 999);
}

/** Start of year in UTC (mirrors date-fns startOfYear but UTC-safe). */
function startOfYearUTC(date: Date): Date {
    return inUTC(date.getUTCFullYear(), 0, 1);
}

/** Start of quarter in UTC (Jan=0, Apr=1, Jul=2, Oct=3). */
function startOfQuarterUTC(date: Date): Date {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    const q = Math.floor(m / 3) * 3;
    return inUTC(y, q, 1);
}

/** Start of month in UTC. */
function startOfMonthUTC(date: Date): Date {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    return inUTC(y, m, 1);
}

/** Last day of month in UTC at 23:59:59.999 (day 0 of next month = last day of current). */
function endOfMonthUTC(date: Date): Date {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    const last = inUTC(y, m + 1, 0);
    return inUTC(
        last.getUTCFullYear(),
        last.getUTCMonth(),
        last.getUTCDate(),
        23,
        59,
        59,
        999,
    );
}

/** End of quarter in UTC. */
function endOfQuarterUTC(date: Date): Date {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    const q = Math.floor(m / 3) * 3;
    const last = inUTC(y, q + 3, 0);
    return inUTC(
        last.getUTCFullYear(),
        last.getUTCMonth(),
        last.getUTCDate(),
        23,
        59,
        59,
        999,
    );
}

/** Previous month in UTC (same day if valid, else last day of that month). */
function subMonthsUTC(date: Date, n: number): Date {
    let y = date.getUTCFullYear();
    let m = date.getUTCMonth() - n;
    while (m < 0) {
        m += 12;
        y -= 1;
    }
    const lastDay = inUTC(y, m + 1, 0).getUTCDate();
    const d = Math.min(date.getUTCDate(), lastDay);
    return inUTC(
        y,
        m,
        d,
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds(),
    );
}

/**
 * Returns the date range for the given preset in GMT.
 * Default preset is YTD.
 */
export function getDateRangeForPreset(
    preset: ReportDatePreset = ReportDatePreset.YTD,
    referenceDate?: Date,
): ReportDateRange {
    const now = referenceDate ?? new Date();
    const today = endOfDayGMT(now);

    switch (preset) {
        case ReportDatePreset.YTD:
            return { from: startOfYearUTC(now), to: today };
        case ReportDatePreset.QTD:
            return { from: startOfQuarterUTC(now), to: today };
        case ReportDatePreset.MTD:
            return { from: startOfMonthUTC(now), to: today };
        case ReportDatePreset.LAST_MONTH: {
            const prev = subMonthsUTC(now, 1);
            return { from: startOfMonthUTC(prev), to: endOfMonthUTC(prev) };
        }
        case ReportDatePreset.LAST_QUARTER: {
            const prev = subMonthsUTC(now, 3);
            return { from: startOfQuarterUTC(prev), to: endOfQuarterUTC(prev) };
        }
        case ReportDatePreset.LAST_12_MONTHS: {
            const start = startOfDayGMT(subMonthsUTC(now, 12));
            return { from: start, to: today };
        }
        default:
            return getDateRangeForPreset(ReportDatePreset.YTD, now);
    }
}

/**
 * Format a date for the reports API (YYYY-MM-DD) in UTC.
 */
export function formatDateForApi(date: Date): string {
    return formatInTimeZone(date, UTC, 'yyyy-MM-dd');
}

/**
 * Format range for API as ISO strings (start of day / end of day GMT).
 */
export function formatRangeForSavingsApi(range: ReportDateRange): {
    dateFrom: string;
    dateTo: string;
} {
    const normalizedFrom = startOfDayGMT(range.from);
    const normalizedTo = endOfDayGMT(range.to);

    return {
        dateFrom: normalizedFrom.toISOString(),
        dateTo: normalizedTo.toISOString(),
    };
}
